import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import apis from '@src/apis';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { SORT_VOICE_OPTIONS_FAVOURITE } from '@src/constants/voice';

// Which voices belong on the educators home is a merchandising choice, not a
// property of the voices themselves, so the list lives in a feature flag and
// this hook only resolves it into voice records.
const useTeachingVoices = () => {
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);

  const [voices, setVoices] = useState([]);
  const [favouriteCodes, setFavouriteCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  // No readiness guard: App.jsx holds the whole tree behind a spinner until
  // the flags have loaded, so anything rendered here already has them.
  const config = getFeatureValue(FEATURE_KEYS.EDUCATOR_HOME_VOICES, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  // getFeatureValue builds a throwaway GrowthBook instance per call, so it
  // hands back a new object every render — without this the effect below would
  // refetch forever.
  const codesKey = JSON.stringify(config?.voiceCodes || []);
  const voiceCodes = useMemo(() => {
    const codes = JSON.parse(codesKey);
    return Array.isArray(codes) ? codes.filter(Boolean) : [];
  }, [codesKey]);

  useEffect(() => {
    if (!voiceCodes.length) {
      setVoices([]);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      try {
        const [voiceData, favouriteData] = await Promise.all([
          apis.voices.getVoicesV3(
            { codes: voiceCodes },
            { signal: controller.signal },
          ),
          apis.favouriteVoices.getFavouriteVoices({
            sort: SORT_VOICE_OPTIONS_FAVOURITE[0].value,
          }),
        ]);

        // The response carries no ordering guarantee, but the order in the flag
        // is the order the section is meant to read in.
        const voicesByCode = new Map(
          (voiceData?.status ? voiceData.result?.voices || [] : []).map(
            (voice) => [voice.code, voice],
          ),
        );
        setVoices(
          voiceCodes
            .map((code) => voicesByCode.get(code))
            // A code that no longer resolves drops its card rather than
            // leaving a blank one behind.
            .filter(Boolean),
        );

        setFavouriteCodes(
          favouriteData?.status
            ? (favouriteData.result?.favouriteVoices || []).map(
                (voice) => voice.code,
              )
            : [],
        );
      } catch {
        // Aborted, or the api layer rejected — the section hides itself.
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [voiceCodes]);

  return { voices, favouriteCodes, setFavouriteCodes, loading };
};

export default useTeachingVoices;
